import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Search,
  User,
  Mail,
  Phone,
  X,
  ChevronRight,
  Calculator,
  Users as UsersIcon,
  TrendingUp,
  Plus,
  Loader2,
  Trash2,
  AlertTriangle,
  BarChart3,
  UserMinus,
  LogIn,
  Database,
  UserPlus,
  Globe,
} from "lucide-react";
import { supabase } from "../../lib/supabase";
import { useToast } from "../../components/Toast";
import { getAccessBadge } from "../../lib/accessStatus";

function DeleteConfirmModal({ student, onConfirm, onClose, deleting }) {
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-xl max-w-md w-full p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
            <AlertTriangle size={24} className="text-red-600" />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900">Eliminar estudiante</h3>
            <p className="text-sm text-gray-500">Esta acción no se puede deshacer</p>
          </div>
        </div>

        <div className="bg-gray-50 rounded-xl p-4 mb-4">
          <p className="font-medium text-gray-900">{student.name}</p>
          <p className="text-sm text-gray-500">{student.email}</p>
        </div>

        <p className="text-sm text-gray-600 mb-6">
          Se eliminarán permanentemente todos los datos del estudiante: progreso,
          simulaciones, leads y documentos.
        </p>

        <div className="flex gap-3">
          <button
            onClick={onClose}
            disabled={deleting}
            className="flex-1 px-4 py-2.5 border border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50 font-medium transition-colors disabled:opacity-50"
          >
            Cancelar
          </button>
          <button
            onClick={onConfirm}
            disabled={deleting}
            className="flex-1 px-4 py-2.5 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium flex items-center justify-center gap-2 transition-colors disabled:opacity-50"
          >
            {deleting ? (
              <Loader2 size={18} className="animate-spin" />
            ) : (
              <>
                <Trash2 size={18} />
                Eliminar
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

function NewStudentModal({ onClose, onSuccess }) {
  const { showSuccess, showError } = useToast();
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) return;

    setSaving(true);
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData?.session?.access_token;
      if (!token) throw new Error("No hay sesión activa");

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/invite-student`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
            apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
          },
          body: JSON.stringify(form),
        }
      );

      const data = await response.json();
      if (!response.ok || data.error)
        throw new Error(data.error || "Error al crear estudiante");

      showSuccess(`Invitación enviada a ${form.email}`);
      onSuccess();
      onClose();
    } catch (err) {
      showError(err.message || "Error al crear estudiante");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-xl max-w-md w-full overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between bg-gradient-to-r from-indigo-600 to-indigo-700">
          <div>
            <h2 className="text-lg font-bold text-white">Nuevo Estudiante</h2>
            <p className="text-sm text-indigo-200">
              Se enviará un email de invitación
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/10 rounded-lg text-white"
          >
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Nombre completo *
            </label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Juan García"
              required
              className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400 outline-none"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email *
            </label>
            <input
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              placeholder="juan@ejemplo.com"
              required
              className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400 outline-none"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Teléfono
            </label>
            <input
              type="tel"
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              placeholder="+34 600 000 000"
              className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400 outline-none"
            />
          </div>

          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2.5 border border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50 font-medium transition-colors"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={saving || !form.name.trim() || !form.email.trim()}
              className="flex-1 px-4 py-2.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 font-medium flex items-center justify-center gap-2 transition-colors"
            >
              {saving ? (
                <Loader2 size={18} className="animate-spin" />
              ) : (
                <>
                  <Mail size={18} />
                  Enviar invitación
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function getWeek(startDate) {
  if (!startDate) return null;
  const diff = Date.now() - new Date(startDate).getTime();
  return Math.min(12, Math.max(1, Math.ceil(diff / (7 * 24 * 60 * 60 * 1000))));
}

function StudentCard({ student, onClick }) {
  const week = getWeek(student.start_date);
  const access = getAccessBadge(student);
  return (
    <div
      onClick={onClick}
      className="bg-white rounded-xl border border-gray-200 p-4 hover:shadow-md hover:border-gray-300 cursor-pointer transition-all"
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-gray-900 truncate">
            {student.name}
          </h3>
          <p className="text-sm text-gray-500 truncate">{student.email}</p>
        </div>
        <ChevronRight size={20} className="text-gray-400 flex-shrink-0" />
      </div>

      <div className="flex items-center justify-between gap-2 mb-3">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="inline-flex px-2 py-0.5 text-xs font-medium rounded-full bg-blue-100 text-blue-700">
            {student.stage || "Activo"}
          </span>
          {student.airtable_id && !student.airtable_id.startsWith("admin_") ? (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-medium rounded-full bg-orange-100 text-orange-700">
              <Database size={10} /> Airtable
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-medium rounded-full bg-indigo-100 text-indigo-700">
              <UserPlus size={10} /> Manual
            </span>
          )}
          <span
            className={`inline-flex px-2 py-0.5 text-[10px] font-medium rounded-full ${access.color}`}
            title="Estado de acceso"
          >
            {access.label}
          </span>
        </div>
        <div className="flex items-center gap-2">
          {week && (
            <span className="text-[10px] font-medium text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded">
              Sem {week}/12
            </span>
          )}
          {student.last_login && (
            <div
              className="flex items-center gap-1 text-xs text-gray-400"
              title="Último acceso"
            >
              <LogIn size={12} />
              <span>
                {new Date(student.last_login).toLocaleDateString("es-ES", {
                  day: "numeric",
                  month: "short",
                })}
              </span>
            </div>
          )}
        </div>
      </div>

      <div className="flex items-center gap-3">
        <div className="flex-1">
          <div className="flex justify-between text-xs mb-1">
            <span className="text-gray-500">Progreso</span>
            <span className="font-medium text-gray-700">
              {student.progress || 0}%
            </span>
          </div>
          <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
            <div
              className={`h-full transition-all rounded-full ${
                student.progress >= 80
                  ? "bg-emerald-500"
                  : student.progress >= 50
                  ? "bg-indigo-500"
                  : student.progress >= 20
                  ? "bg-amber-500"
                  : "bg-gray-300"
              }`}
              style={{ width: `${student.progress || 0}%` }}
            />
          </div>
        </div>
      </div>

      {(student.simulations_count > 0 ||
        student.leads_count > 0 ||
        student.bajas_count > 0 ||
        student.metrics_count > 0 ||
        student.lead_form_status) && (
        <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-gray-100">
          {student.simulations_count > 0 && (
            <div className="flex items-center gap-1 text-xs text-blue-600 bg-blue-50 px-2 py-0.5 rounded-full">
              <Calculator size={12} />
              <span>{student.simulations_count}</span>
            </div>
          )}
          {student.leads_count > 0 && (
            <div className="flex items-center gap-1 text-xs text-purple-600 bg-purple-50 px-2 py-0.5 rounded-full">
              <UsersIcon size={12} />
              <span>{student.leads_count}</span>
            </div>
          )}
          {student.bajas_count > 0 && (
            <div className="flex items-center gap-1 text-xs text-red-600 bg-red-50 px-2 py-0.5 rounded-full">
              <UserMinus size={12} />
              <span>{student.bajas_count}</span>
            </div>
          )}
          {student.metrics_count > 0 && (
            <div className="flex items-center gap-1 text-xs text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full">
              <BarChart3 size={12} />
              <span>{student.metrics_count}</span>
            </div>
          )}
          {student.lead_form_status === "active" && (
            <div
              className="flex items-center gap-1 text-xs text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full"
              title="Buzón web activo"
            >
              <Globe size={12} />
              <span>Buzón</span>
            </div>
          )}
          {student.lead_form_status === "paused" && (
            <div
              className="flex items-center gap-1 text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full"
              title="Buzón web pausado"
            >
              <Globe size={12} />
              <span>Buzón pausado</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function AdminStudents() {
  const navigate = useNavigate();
  const { showError, showSuccess } = useToast();
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterStage, setFilterStage] = useState("");
  const [filterOrigin, setFilterOrigin] = useState("");
  const [filterWeek, setFilterWeek] = useState("");
  const [filterInbox, setFilterInbox] = useState("");
  const [showNewModal, setShowNewModal] = useState(false);
  const [studentToDelete, setStudentToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const fetchStudents = async () => {
    const [
      studentsRes, simsRes, leadsRes, bajasRes, metricsRes, loginsRes, inboxRes,
    ] = await Promise.all([
      supabase
        .from("students")
        .select("*")
        .order("created_at", { ascending: false }),
      supabase.from("simulations").select("student_id"),
      supabase.from("leads").select("student_id"),
      supabase.from("bajas").select("student_id"),
      supabase.from("monthly_data").select("student_id"),
      supabase.from("login_history").select("student_id, logged_in_at"),
      supabase
        .from("lead_capture_forms")
        .select("student_id, is_active"),
    ]);

    if (studentsRes.error) {
      showError("Error al cargar alumnos");
      setLoading(false);
      return;
    }

    const simMap = {};
    const leadMap = {};
    const bajasMap = {};
    const metricsMap = {};
    const loginsMap = {};
    const inboxMap = {};

    (simsRes.data || []).forEach((s) => {
      simMap[s.student_id] = (simMap[s.student_id] || 0) + 1;
    });
    (leadsRes.data || []).forEach((l) => {
      leadMap[l.student_id] = (leadMap[l.student_id] || 0) + 1;
    });
    (bajasRes.data || []).forEach((b) => {
      bajasMap[b.student_id] = (bajasMap[b.student_id] || 0) + 1;
    });
    (metricsRes.data || []).forEach((m) => {
      metricsMap[m.student_id] = (metricsMap[m.student_id] || 0) + 1;
    });
    (loginsRes.data || []).forEach((l) => {
      if (!loginsMap[l.student_id]) {
        loginsMap[l.student_id] = { count: 0, last: null };
      }
      loginsMap[l.student_id].count += 1;
      const date = new Date(l.logged_in_at);
      if (!loginsMap[l.student_id].last || date > loginsMap[l.student_id].last) {
        loginsMap[l.student_id].last = date;
      }
    });
    (inboxRes.data || []).forEach((f) => {
      inboxMap[f.student_id] = f.is_active ? "active" : "paused";
    });

    const enriched = (studentsRes.data || []).map((s) => ({
      ...s,
      simulations_count: simMap[s.id] || 0,
      leads_count: leadMap[s.id] || 0,
      bajas_count: bajasMap[s.id] || 0,
      metrics_count: metricsMap[s.id] || 0,
      logins_count: loginsMap[s.id]?.count || 0,
      last_login: loginsMap[s.id]?.last || null,
      lead_form_status: inboxMap[s.id] || null,
    }));

    setStudents(enriched);
    setLoading(false);
  };

  useEffect(() => {
    fetchStudents();
  }, []);

  const handleDeleteStudent = async () => {
    if (!studentToDelete) return;

    setDeleting(true);
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData?.session?.access_token;
      if (!token) throw new Error("No hay sesión activa");

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/delete-student`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
            apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
          },
          body: JSON.stringify({ student_id: studentToDelete.id }),
        }
      );

      const data = await response.json();
      if (!response.ok || data.error)
        throw new Error(data.error || "Error al eliminar");

      showSuccess("Estudiante eliminado correctamente");
      setStudentToDelete(null);
      fetchStudents();
    } catch (err) {
      showError(err.message || "Error al eliminar estudiante");
    } finally {
      setDeleting(false);
    }
  };

  const filteredStudents = students.filter((s) => {
    if (search) {
      const q = search.toLowerCase();
      if (
        !s.name?.toLowerCase().includes(q) &&
        !s.email?.toLowerCase().includes(q)
      )
        return false;
    }
    if (filterStage && s.stage !== filterStage) return false;
    if (filterOrigin) {
      const isAirtable = s.airtable_id && !s.airtable_id.startsWith("admin_");
      if (filterOrigin === "airtable" && !isAirtable) return false;
      if (filterOrigin === "manual" && isAirtable) return false;
    }
    if (filterWeek) {
      const week = getWeek(s.start_date);
      if (!week) return false;
      if (filterWeek === "1-4" && (week < 1 || week > 4)) return false;
      if (filterWeek === "5-8" && (week < 5 || week > 8)) return false;
      if (filterWeek === "9-12" && (week < 9 || week > 12)) return false;
    }
    if (filterInbox) {
      if (filterInbox === "active"  && s.lead_form_status !== "active")  return false;
      if (filterInbox === "paused"  && s.lead_form_status !== "paused")  return false;
      if (filterInbox === "none"    && s.lead_form_status !== null)      return false;
    }
    return true;
  });

  const stages = [...new Set(students.map((s) => s.stage).filter(Boolean))];
  const hasActiveFilters =
    filterStage || filterOrigin || filterWeek || filterInbox;

  const totalProgress =
    students.length > 0
      ? Math.round(
          students.reduce((sum, s) => sum + (s.progress || 0), 0) /
            students.length
        )
      : 0;

  if (loading) {
    return (
      <div className="animate-pulse space-y-4">
        <div className="h-8 w-48 bg-gray-200 rounded" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-24 bg-gray-200 rounded-xl" />
          ))}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div key={i} className="h-40 bg-gray-200 rounded-xl" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center">
              <User className="text-indigo-600" size={20} />
            </div>
            <div>
              <p className="text-sm text-gray-500">Total alumnos</p>
              <p className="text-2xl font-bold text-gray-900">
                {students.length}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
              <TrendingUp className="text-emerald-600" size={20} />
            </div>
            <div>
              <p className="text-sm text-gray-500">Progreso promedio</p>
              <p className="text-2xl font-bold text-gray-900">
                {totalProgress}%
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
              <Calculator className="text-purple-600" size={20} />
            </div>
            <div>
              <p className="text-sm text-gray-500">Usando herramientas</p>
              <p className="text-2xl font-bold text-gray-900">
                {
                  students.filter(
                    (s) => s.simulations_count > 0 || s.leads_count > 0
                  ).length
                }
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-3">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="flex-1 relative">
            <Search
              size={18}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
            />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar por nombre o email..."
              className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400 outline-none bg-white"
            />
          </div>
          <button
            onClick={() => setShowNewModal(true)}
            className="px-4 py-2.5 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 transition-colors font-medium flex items-center gap-2"
          >
            <Plus size={18} />
            <span className="hidden sm:inline">Nuevo Estudiante</span>
          </button>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <select
            value={filterStage}
            onChange={(e) => setFilterStage(e.target.value)}
            className="px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400 outline-none"
          >
            <option value="">Estado</option>
            {stages.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
          <select
            value={filterOrigin}
            onChange={(e) => setFilterOrigin(e.target.value)}
            className="px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400 outline-none"
          >
            <option value="">Origen</option>
            <option value="airtable">Airtable</option>
            <option value="manual">Manual</option>
          </select>
          <select
            value={filterWeek}
            onChange={(e) => setFilterWeek(e.target.value)}
            className="px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400 outline-none"
          >
            <option value="">Semana</option>
            <option value="1-4">Sem 1-4</option>
            <option value="5-8">Sem 5-8</option>
            <option value="9-12">Sem 9-12</option>
          </select>
          <select
            value={filterInbox}
            onChange={(e) => setFilterInbox(e.target.value)}
            className="px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400 outline-none"
          >
            <option value="">Buzón web</option>
            <option value="active">Activo</option>
            <option value="paused">Pausado</option>
            <option value="none">Sin buzón</option>
          </select>
          {hasActiveFilters && (
            <button
              onClick={() => {
                setFilterStage("");
                setFilterOrigin("");
                setFilterWeek("");
                setFilterInbox("");
              }}
              className="px-3 py-2 text-xs font-medium text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            >
              Limpiar filtros
            </button>
          )}
        </div>
      </div>

      <p className="text-sm text-gray-500">
        {filteredStudents.length} alumno
        {filteredStudents.length !== 1 ? "s" : ""} encontrado
        {filteredStudents.length !== 1 ? "s" : ""}
      </p>

      {filteredStudents.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 px-4 py-12 text-center">
          <User size={48} className="mx-auto text-gray-300 mb-4" />
          <p className="text-gray-500">No se encontraron alumnos</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredStudents.map((student) => (
            <StudentCard
              key={student.id}
              student={student}
              onClick={() => navigate(`/admin/alumnos/${student.id}`)}
            />
          ))}
        </div>
      )}

      {showNewModal && (
        <NewStudentModal
          onClose={() => setShowNewModal(false)}
          onSuccess={() => fetchStudents()}
        />
      )}

      {studentToDelete && (
        <DeleteConfirmModal
          student={studentToDelete}
          onConfirm={handleDeleteStudent}
          onClose={() => setStudentToDelete(null)}
          deleting={deleting}
        />
      )}
    </div>
  );
}
